import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDialog,
  Separator,
  showNotification,
  UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField,
} from 'platform/components';
import {Box, Hide, HStack, Show, Text, VStack} from 'platform/foundation';
import * as Yup from 'yup';

import {useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {equals, isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useUploadFileMutation} from '@dms/api/base';
import {
  useCreateWatermarkV2Mutation,
  useGetAdvertisingStatisticsQuery,
  useGetWatermarkDetailQuery,
  useGetWatermarkListQuery,
  useUpdateWatermarkV2Mutation,
} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {useNavigate, yupString} from 'shared/web';

import {CompositionGraphicSection} from '../../../components/CompositionEditor/components/CompositionGraphicSection';
import {toCompositionScene} from '../../../components/CompositionEditor/utils/toCompositionScene';
import {openRepublishInfoDialog} from '../../../components/RepublishInfoDialogContent/utils/openRepublishInfoDialog';
import {useGetPreviewPhotoUrl} from '../hooks/useGetPreviewPhotoUrl';
import {WatermarkFormType} from '../types/WatermarkFormType';
import {composeOnPreviewPhoto} from '../utils/composeOnPreviewPhoto';
import {getDefaultValue} from '../utils/getDefaultValue';
import {getRequestData} from '../utils/getRequestData';
import {isWatermarkBodyEqual} from '../utils/isWatermarkBodyEqual';
import {addToOptions, getPositionOptions} from '../utils/radioOptions';
import {WatermarkDetailPreview} from './WatermarkDetailPreview';

export function WatermarkForm() {
  const {id} = useParams();
  const navigate = useNavigate();
  // Server render of the composition applied in this session; cleared when the graphic is removed.
  const [compositionPreviewUrl, setCompositionPreviewUrl] = useState<string | null>(null);
  // The render is being composed onto the preview photo: the preview shows loading, not empty.
  const [isComposingPreview, setIsComposingPreview] = useState(false);
  // Each apply or remove gets a new number, so a slow compose cannot overwrite a newer preview.
  const compositionPreviewRequestRef = useRef(0);
  const getPreviewPhotoUrl = useGetPreviewPhotoUrl();

  const [uploadFile] = useUploadFileMutation();
  const {data: advertisingStatistics} = useGetAdvertisingStatisticsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [createWatermarkV2Mutation, {isLoading: isCreatingWatermarkV2}] =
    useCreateWatermarkV2Mutation();
  const [updateWatermarkV2Mutation, {isLoading: isUpdatingWatermarkV2}] =
    useUpdateWatermarkV2Mutation();

  const {data: watermarkData} = useGetWatermarkDetailQuery(
    {watermarkId: id ?? ''},
    {skip: isNilOrEmpty(id)}
  );
  const {data: watermarkList, isLoading: isLoadingWatermarkList} = useGetWatermarkListQuery();

  const handleUpload = async (file: File) => {
    const data = await uploadFile({file}).unwrap();

    return {id: data.fileId, url: data.fileUri};
  };

  // The render holds only the graphic; it is shown on the sample photo like a saved watermark.
  // When the composing fails, the bare render is still a better preview than none.
  const showCompositionPreview = (previewUrl: string | null) => {
    compositionPreviewRequestRef.current += 1;
    const request = compositionPreviewRequestRef.current;
    setCompositionPreviewUrl(null);
    if (isNil(previewUrl)) {
      setIsComposingPreview(false);
      return;
    }
    setIsComposingPreview(true);
    getPreviewPhotoUrl()
      .then((photoUrl) => composeOnPreviewPhoto(photoUrl, previewUrl))
      .catch(() => previewUrl)
      .then((url) => {
        if (request === compositionPreviewRequestRef.current) {
          setCompositionPreviewUrl(url);
          setIsComposingPreview(false);
        }
      });
  };

  const isLoading = isLoadingWatermarkList || isCreatingWatermarkV2 || isUpdatingWatermarkV2;

  const handleSubmit: FormSubmitHandler<WatermarkFormType> = async (data) => {
    const isBodyEqual = watermarkData ? isWatermarkBodyEqual(data, watermarkData) : true;

    const createWatermark = ({
      setToAllVehicles,
      showRepublishDialog,
    }: {
      setToAllVehicles: boolean;
      showRepublishDialog: boolean;
    }) => {
      const handleRepublishInfoDialogOpen = () => {
        if (
          showRepublishDialog &&
          isNotNil(advertisingStatistics?.inAdvertisementCount) &&
          advertisingStatistics.inAdvertisementCount > 0
        ) {
          openRepublishInfoDialog({
            onCloseComplete: () => {
              navigate(settingsRoutes.advertisingWatermarking);
            },
          });
        } else {
          navigate(settingsRoutes.advertisingWatermarking);
        }
      };
      if (id) {
        return updateWatermarkV2Mutation({
          watermarkId: id,
          updateWatermarkV2RequestBody: {
            ...getRequestData(data),
          },
        })
          .unwrap()
          .then(() => {
            handleRepublishInfoDialogOpen();
          })
          .catch(handleApiError);
      } else {
        return createWatermarkV2Mutation({
          createWatermarkV2RequestBody: {
            ...getRequestData(data),
            setToAllVehicles,
          },
        })
          .unwrap()
          .then(() => {
            handleRepublishInfoDialogOpen();
          })
          .catch(handleApiError);
      }
    };

    if (watermarkList && watermarkList.length > 0 && !id) {
      openDialog(
        <VStack spacing={1}>
          <Text>{i18n.t('entity.watermark.setVehicleDialog.description')}</Text>
          <ButtonGroup align="right">
            <Button
              data-testid="setVehicles-watermark-dialog-cancel"
              variant="secondary"
              onClick={() => {
                createWatermark({
                  setToAllVehicles: false,
                  showRepublishDialog: false,
                });
              }}
              isDisabled={isLoading}
              title={i18n.t('general.actions.cancel')}
            />
            <Button
              data-testid="setVehicles-watermark-dialog-confirm"
              variant="primary"
              onClick={() => {
                createWatermark({
                  setToAllVehicles: true,
                  showRepublishDialog: true,
                });
              }}
              isLoading={isLoading}
              title={i18n.t('general.actions.confirm')}
            />
          </ButtonGroup>
        </VStack>,
        {
          'data-testid': 'setVehicles-watermark-dialog',
          id: 'watermark-setVehiclesDialog',
          title: i18n.t('entity.watermark.setVehicleDialog.title'),
        }
      );
    }
    if (watermarkList && watermarkList.length < 1 && !id) {
      openDialog(
        <VStack spacing={1}>
          <Text>{i18n.t('entity.watermark.setNewVehicleDialog.description')}</Text>
          <ButtonGroup align="right">
            <Button
              data-testid="setVehicles-newWatermark-dialog-cancel"
              variant="secondary"
              onClick={() => {
                createWatermark({
                  setToAllVehicles: false,
                  showRepublishDialog: false,
                });
              }}
              isDisabled={isLoading}
              title={i18n.t('general.actions.cancel')}
            />
            <Button
              data-testid="setVehicles-newWatermark-dialog-confirm"
              variant="primary"
              onClick={() => {
                createWatermark({
                  setToAllVehicles: true,
                  showRepublishDialog: true,
                });
              }}
              isLoading={isLoading}
              title={i18n.t('general.actions.confirm')}
            />
          </ButtonGroup>
        </VStack>,
        {
          'data-testid': 'setVehicles-newWatermark-dialog',
          id: 'watermark-setVehiclesDialog-new',
          title: i18n.t('entity.watermark.setNewVehicleDialog.title'),
        }
      );
    }
    if (id && !isBodyEqual) {
      createWatermark({
        setToAllVehicles: false,
        showRepublishDialog: true,
      });
    }
    if (id && isBodyEqual) {
      await createWatermark({
        setToAllVehicles: false,
        showRepublishDialog: false,
      });
    }
  };

  const handleDiscard = () => navigate(settingsRoutes.advertisingWatermarking);

  const handleUploadError = (error: Error) => {
    if (error instanceof UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField) {
      showNotification.error(i18n.t('entity.watermark.notifications.imageFormatIsNotAllowed'));
    }
  };

  return (
    <Form<WatermarkFormType>
      onSubmit={handleSubmit}
      schema={WatermarkFormSchema}
      defaultValues={getDefaultValue(watermarkData)}
    >
      {(control, formApi) => {
        const composition = formApi.watch('composition') ?? null;
        const hasComposition = isNotNil(composition);

        return (
          <VStack spacing={3}>
            <Box maxWidth="50%">
              <FormField
                label={i18n.t('page.salesSettings.backgroundRemoval.configurationName')}
                control={control}
                name="name"
                type="text"
                isRequired
                data-testid="watermarks-configurationName"
              />
            </Box>

            <Separator spacing={2} />

            <FormField
              type="uploadImage"
              control={control}
              name="fileId"
              uploadFileRequest={handleUpload}
              onUploadError={handleUploadError}
              helperText={i18n.t('entity.photo.labels.uploadOrDragAndDropImage')}
              uploadingText={i18n.t('general.labels.uploadingWithDots')}
              uploadIcon="image/photo_camera"
              linkValueFormatter={(url) => url.replace('resize', 'get')}
              srcValueFormatter={(url) => `${url}${url.includes('resize') ? '&height=108' : ''}`}
              onPreviewClick={(url) =>
                window.open(`${url.replace('resize', 'get')}&type=inline`, '_blank')
              }
              isRequired={!hasComposition}
              data-testid="settings-advertising-watermarking-uploadImage"
            />
            <Show whenFeatureEnabled={featureFlags.COMPOSITION_EDITOR}>
              <Separator />
              <CompositionGraphicSection
                composition={composition}
                onApply={(result) => {
                  const scene = toCompositionScene(result.scene);
                  if (isNil(scene)) {
                    showNotification.error();
                    return;
                  }
                  formApi.setValue('composition', scene, {shouldDirty: true});
                  showCompositionPreview(result.previewUrl);
                }}
                onRemove={() => {
                  formApi.setValue('composition', null, {shouldDirty: true});
                  showCompositionPreview(null);
                }}
                data-testid="settings-advertising-watermarking-graphic"
              />
            </Show>
            <Hide when={hasComposition}>
              <Separator />
              <HStack align="center" spacing={4}>
                <VStack spacing={2} width={80}>
                  <FormField
                    type="slider"
                    name="size"
                    control={control}
                    min={0}
                    max={100}
                    step={1}
                    ticks={2}
                    label={i18n.t('entity.watermark.labels.size')}
                    isDisabled={formApi.watch('fitToImage')}
                    data-testid="settings-advertising-watermarking-form-size"
                  />
                </VStack>
                <FormField
                  type="checkbox"
                  name="fitToImage"
                  control={control}
                  label={i18n.t('entity.watermark.labels.fitToImage')}
                  data-testid="settings-advertising-watermarking-form-fitToImage"
                />
              </HStack>
              <Separator />
              <VStack spacing={4}>
                <FormField
                  label={i18n.t('entity.watermark.labels.position')}
                  type="radio"
                  name="position"
                  control={control}
                  options={getPositionOptions('top')}
                  data-testid="settings-advertising-watermarking-form-position-top"
                />
                <FormField
                  type="radio"
                  name="position"
                  control={control}
                  options={getPositionOptions('center')}
                  data-testid="settings-advertising-watermarking-form-position-center"
                />
                <FormField
                  type="radio"
                  name="position"
                  control={control}
                  options={getPositionOptions('bottom')}
                  data-testid="settings-advertising-watermarking-form-position-bottom"
                />
              </VStack>
            </Hide>
            <Separator />
            <FormField
              type="radio"
              name="usageType"
              control={control}
              options={addToOptions.filter((option) =>
                option.requiredFeatureFlag ? isFeatureEnabled(option.requiredFeatureFlag) : true
              )}
              data-testid="settings-advertising-watermarking-form-usageType"
            />
            <Separator />
            <VStack spacing={4}>
              <WatermarkDetailPreview
                watermarkData={getRequestData(formApi.watch())}
                compositionPreviewUrl={hasComposition ? compositionPreviewUrl : null}
                isComposingPreview={hasComposition && isComposingPreview}
                isCompositionUnsaved={
                  hasComposition && !equals(composition, watermarkData?.composition ?? null)
                }
              />
            </VStack>
            <Separator />
            <VStack justify="flex-end">
              <ButtonGroup>
                <Button
                  variant="secondary"
                  onClick={handleDiscard}
                  title={i18n.t('general.actions.discard')}
                  data-testid="settings-advertising-watermarking-form-discard-btn"
                />
                <FormButton
                  type="submit"
                  title={i18n.t('general.actions.save')}
                  control={control}
                  data-testid="settings-advertising-watermarking-form-save-btn"
                />
              </ButtonGroup>
            </VStack>
          </VStack>
        );
      }}
    </Form>
  );
}

// The uploaded image is required unless a composition is applied: the BE then renders the
// watermark file from the composition and ignores `fileId`.
const WatermarkFormSchema = Yup.object({
  name: yupString.required(),
  composition: Yup.mixed().nullable(),
  fileId: Yup.mixed()
    .nullable()
    .when('composition', {
      is: (composition: unknown) => isNil(composition),
      then: (schema) =>
        schema.test(
          'fileId-required',
          i18n.t('general.errors.mixed.required'),
          (value) => value !== null && value !== undefined
        ),
    }),
  size: Yup.number().required(),
  position: Yup.string().required(),
  fitToImage: Yup.boolean(),
  usageType: Yup.string().required(),
});
